import { useCallback, useContext, useEffect, useReducer, useState } from 'react'

import { Search, Close } from '@mui/icons-material'
import { Box, Typography, Button, IconButton } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import toast from 'react-hot-toast'
import type { OpportunityCreateInput } from 'types/graphql'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import ObjectTile from 'src/components/ObjectTile/ObjectTile'
import Row from 'src/components/Row/Row'
import SearchModal from 'src/components/Search/SearchModal/SearchModal'
import {
  extractEmailDomain,
  NativeObjectFormatters,
} from 'src/lib/contactFormatting'
import { DayContext } from 'src/lib/dayContext'
import { dayjs } from 'src/lib/dayjs'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'

import { CREATE_OPPORTUNITY_FOR_DIALOG } from '../SidebarObjectCreate/mutations'
import OpportunityCreateForm from '../SidebarObjectCreate/OpportunityCreateForm'

// Types
interface OpportunityState {
  title: string
  oppType: string
  selectedOwner: any | null
  ownerWasDismissed: boolean
  selectedPipeline: any | null
  selectedStage: any | null
  selectedOrganization: any | null
  selectedPerson: any | null
  expectedCloseDate: Date
  expectedRevenue: number | null
  hasRevenue: boolean
  domain: string | null
}

type OpportunityAction =
  | { type: 'SET_TITLE'; payload: string }
  | { type: 'SET_OPP_TYPE'; payload: string }
  | { type: 'SET_OWNER'; payload: any }
  | { type: 'DISMISS_OWNER' }
  | { type: 'SET_PIPELINE'; payload: any }
  | { type: 'SET_STAGE'; payload: any }
  | { type: 'SET_ORGANIZATION'; payload: any }
  | { type: 'SET_PERSON'; payload: any }
  | { type: 'SET_EXPECTED_CLOSE_DATE'; payload: Date }
  | { type: 'SET_EXPECTED_REVENUE'; payload: number | null }
  | { type: 'SET_HAS_REVENUE'; payload: boolean }
  | { type: 'SET_DOMAIN'; payload: string | null }
  | { type: 'RESET' }
  | { type: 'INITIALIZE'; payload: Partial<OpportunityState> }

const initialOpportunityState: OpportunityState = {
  title: '',
  oppType: '',
  selectedOwner: null,
  ownerWasDismissed: false,
  selectedPipeline: null,
  selectedStage: null,
  selectedOrganization: null,
  selectedPerson: null,
  expectedCloseDate: new Date(),
  expectedRevenue: null,
  hasRevenue: false,
  domain: null,
}

// Reducer
function opportunityReducer(
  state: OpportunityState,
  action: OpportunityAction
): OpportunityState {
  switch (action.type) {
    case 'SET_ORGANIZATION': {
      logger.dev('Setting organization:', action.payload)
      // Ensure domain is properly set when organization is selected
      const orgDomain =
        action.payload?.domain || action.payload?.objectId || null
      // Always update title with org name or domain when organization is selected
      const orgTitle = action.payload?.name || orgDomain || ''
      return {
        ...state,
        selectedOrganization: action.payload,
        domain: orgDomain,
        title: orgTitle,
      }
    }
    case 'SET_PERSON': {
      // Only set title from person if no organization is selected and no title exists
      const personTitle =
        !state.selectedOrganization && !state.title
          ? NativeObjectFormatters[NativeObjectTypes.Contact]?.label?.(
              action.payload
            ) || action.payload.objectId
          : state.title
      return {
        ...state,
        selectedPerson: action.payload,
        title: personTitle,
      }
    }
    case 'SET_PIPELINE':
      return {
        ...state,
        selectedPipeline: action.payload,
        oppType: action.payload.opportunityTypes?.[0] || 'New Business',
        hasRevenue: action.payload.hasRevenue,
      }
    case 'SET_STAGE':
      return {
        ...state,
        selectedStage: action.payload,
      }
    case 'SET_OWNER':
      return {
        ...state,
        selectedOwner: action.payload,
        ownerWasDismissed: false,
      }
    case 'SET_TITLE':
      return {
        ...state,
        title: action.payload,
      }
    case 'SET_OPP_TYPE':
      return {
        ...state,
        oppType: action.payload,
      }
    case 'SET_EXPECTED_CLOSE_DATE':
      return {
        ...state,
        expectedCloseDate: action.payload,
      }
    case 'SET_EXPECTED_REVENUE':
      return {
        ...state,
        expectedRevenue: action.payload,
      }
    case 'SET_HAS_REVENUE':
      return {
        ...state,
        hasRevenue: action.payload,
      }
    case 'SET_DOMAIN':
      return {
        ...state,
        domain: action.payload,
      }
    case 'DISMISS_OWNER':
      return {
        ...state,
        selectedOwner: null,
        ownerWasDismissed: true,
      }
    case 'RESET':
      return initialOpportunityState
    case 'INITIALIZE':
      return {
        ...initialOpportunityState,
        ...action.payload,
      }
    default:
      return state
  }
}

// Styles
const STYLES = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    p: 2,
  },
  headerRow: {
    justifyContent: 'space-between',
  },
  content: {
    p: 2,
    flexGrow: 1,
    overflowY: 'auto',
  },
  finderBox: {
    mb: 1,
    mt: 1,
  },
  selectedObject: {
    mb: 2,
  },
  objectTileContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  objectTileBox: {
    width: '300px',
  },
} as const

interface OpportunityEditorProps {
  mode?: 'create' | 'edit'
  initialData?: any
  workspaceId: string
  onComplete?: () => void
  onUpdate?: (newObject: any) => void
}

const OpportunityEditor = ({
  mode = 'create',
  initialData = null,
  workspaceId,
  onComplete,
  onUpdate: _onUpdate,
}: OpportunityEditorProps) => {
  const { currentUser: user } = useAuth()
  const {
    selectedWorkspace,
    workspaces,
    pipelines: contextPipelines,
    orgsByDomain,
    setSidebarObject,
  } = useContext(DayContext)
  const confirm = useConfirm()

  const [searchOpen, setSearchOpen] = useState(false)

  // Initialize state with current user as owner
  const initialStateWithUser = {
    ...initialOpportunityState,
    selectedOwner: user || null,
  }

  const [state, dispatch] = useReducer(opportunityReducer, initialStateWithUser)

  const [createOpportunityForDialog] = useMutation(
    CREATE_OPPORTUNITY_FOR_DIALOG
  )

  // Initialize with initial data if provided
  useEffect(() => {
    if (mode === 'create' && !initialData && contextPipelines?.length > 0) {
      // For create mode, initialize with first pipeline and stage
      const firstPipeline = contextPipelines[0]
      const initialPayload: Partial<OpportunityState> = {
        selectedPipeline: firstPipeline,
        selectedStage: firstPipeline.stages?.[0] || null,
        oppType: firstPipeline.opportunityTypes?.[0] || 'New Business',
        hasRevenue: firstPipeline.hasRevenue || false,
        expectedCloseDate: dayjs().add(60, 'days').toDate(),
      }
      dispatch({ type: 'INITIALIZE', payload: initialPayload })
    } else if (initialData && mode === 'edit') {
      const workspace = workspaces?.find((ws) => ws.id === workspaceId)

      const initialPayload: Partial<OpportunityState> = {
        title: initialData.title || '',
        oppType: initialData.type || 'New Business',
        domain: initialData.domain || null,
        expectedCloseDate: initialData.expectedCloseDate
          ? new Date(initialData.expectedCloseDate)
          : new Date(),
        expectedRevenue: initialData.expectedRevenue || null,
        hasRevenue: !!initialData.expectedRevenue,
      }

      // Set selected organization if domain exists
      if (initialData.selectedOrganization) {
        initialPayload.selectedOrganization = initialData.selectedOrganization
      } else if (initialData.domain && orgsByDomain[initialData.domain]) {
        initialPayload.selectedOrganization = orgsByDomain[initialData.domain]
      }

      // Set selected person
      if (initialData.selectedPerson) {
        initialPayload.selectedPerson = initialData.selectedPerson
      }

      // Set pipeline and stage
      if (initialData.pipelineId && contextPipelines?.length > 0) {
        const pipeline = contextPipelines.find(
          (p) => p.id === initialData.pipelineId
        )
        if (pipeline) {
          initialPayload.selectedPipeline = pipeline
          if (initialData.stageId && pipeline.stages) {
            const stage = pipeline.stages.find(
              (s) => s.id === initialData.stageId
            )
            if (stage) {
              initialPayload.selectedStage = stage
            }
          }
        }
      }

      // Set owner
      if (initialData.selectedOwner) {
        initialPayload.selectedOwner = initialData.selectedOwner
      } else if (initialData.ownerEmail && workspace?.members) {
        const owner = workspace.members.find(
          (member) => member.email === initialData.ownerEmail
        )
        if (owner) {
          initialPayload.selectedOwner = owner
        }
      }

      logger.dev('Initializing opportunity editor with:', initialPayload)
      dispatch({ type: 'INITIALIZE', payload: initialPayload })
    }
  }, [
    initialData,
    mode,
    workspaceId,
    workspaces,
    orgsByDomain,
    contextPipelines,
    user,
  ])

  // Load full pipeline and stage data when initialized with IDs
  useEffect(() => {
    if (state.selectedPipeline?.id && contextPipelines?.length > 0) {
      const fullPipeline = contextPipelines.find(
        (p) => p.id === state.selectedPipeline.id
      )
      if (fullPipeline && fullPipeline.id !== state.selectedPipeline.id) {
        dispatch({ type: 'SET_PIPELINE', payload: fullPipeline })

        // If we have a stage ID, find and set the full stage data
        if (state.selectedStage?.id && fullPipeline.stages?.length > 0) {
          const fullStage = fullPipeline.stages.find(
            (s) => s.id === state.selectedStage.id
          )
          if (fullStage) {
            dispatch({ type: 'SET_STAGE', payload: fullStage })
          }
        }
      }
    }
  }, [state.selectedPipeline?.id, state.selectedStage?.id, contextPipelines])

  // Handle search modal selection
  const handleSearchSelect = useCallback(
    async (result: any) => {
      logger.dev('Search result selected:', result)

      if (result.objectType === NativeObjectTypes.Organization) {
        // For organizations, objectId is the domain
        const domain = result.objectId
        const org = orgsByDomain[domain] || {
          domain: domain,
          objectId: domain,
          name: result.properties?.name || domain,
          ...result.properties,
        }

        logger.dev('Setting organization:', org)
        dispatch({ type: 'SET_ORGANIZATION', payload: org })

        // Set owner from org roles if not already set or dismissed
        if (!state.selectedOwner && !state.ownerWasDismissed) {
          const suggestedOwner = org?.overview?.['objective/roles']?.find(
            (role) => role.role.includes('OWNER_EMAIL')
          )

          if (suggestedOwner) {
            const workspace = workspaces?.find(
              (workspace) => workspace.id === selectedWorkspace
            )
            const workspaceMember = workspace?.members?.find(
              (member) => member.email === suggestedOwner.email
            )
            if (workspaceMember) {
              dispatch({ type: 'SET_OWNER', payload: workspaceMember })
            }
          } else if (user) {
            dispatch({ type: 'SET_OWNER', payload: user })
          }
        }

        // Set expected close date if not set
        if (
          !state.expectedCloseDate ||
          state.expectedCloseDate.getTime() === new Date().getTime()
        ) {
          let offset = 60
          if (org?.overview?.['status/recommendedStage']?.stageNumber) {
            offset -= org.overview['status/recommendedStage'].stageNumber * 7
          }
          dispatch({
            type: 'SET_EXPECTED_CLOSE_DATE',
            payload: dayjs().add(offset, 'days').toDate(),
          })
        }

        // Initialize pipeline if not set
        if (!state.selectedPipeline && contextPipelines?.length > 0) {
          const defaultPipeline = contextPipelines[0]
          if (defaultPipeline) {
            dispatch({ type: 'SET_PIPELINE', payload: defaultPipeline })
            if (defaultPipeline.stages?.length > 0) {
              dispatch({
                type: 'SET_STAGE',
                payload: defaultPipeline.stages[0],
              })
            }
          }
        }
      } else if (result.objectType === NativeObjectTypes.Contact) {
        dispatch({ type: 'SET_PERSON', payload: result })

        // Set title if not set and no org
        if (!state.title && !state.selectedOrganization) {
          const fullName =
            NativeObjectFormatters[result.objectType].label(result)
          dispatch({
            type: 'SET_TITLE',
            payload: fullName || result.objectId,
          })
        }

        // Set owner for person without domain
        if (
          !state.selectedOwner &&
          !extractEmailDomain(result?.objectId) &&
          !state.ownerWasDismissed &&
          user
        ) {
          dispatch({ type: 'SET_OWNER', payload: user })
        }

        // Initialize pipeline if not set
        if (!state.selectedPipeline && contextPipelines?.length > 0) {
          const defaultPipeline = contextPipelines[0]
          if (defaultPipeline) {
            dispatch({ type: 'SET_PIPELINE', payload: defaultPipeline })
            if (defaultPipeline.stages?.length > 0) {
              dispatch({
                type: 'SET_STAGE',
                payload: defaultPipeline.stages[0],
              })
            }
          }
        }
      }

      setSearchOpen(false)
    },
    [
      selectedWorkspace,
      user,
      workspaces,
      orgsByDomain,
      contextPipelines,
      state.title,
      state.selectedOwner,
      state.ownerWasDismissed,
      state.selectedOrganization,
      state.selectedPipeline,
      state.expectedCloseDate,
    ]
  )

  // Handle opportunity creation
  const handleOpportunityCreate = useCallback(async () => {
    // Validate required fields
    if (!state.title?.trim()) {
      toast.error('Please enter an opportunity name')
      return
    }

    if (!state.selectedOwner) {
      toast.error('Please select an owner')
      return
    }

    if (!state.selectedPipeline) {
      toast.error('Please select a pipeline')
      return
    }

    if (!state.selectedStage) {
      toast.error('Please select a stage')
      return
    }

    if (!state.selectedOrganization && !state.selectedPerson) {
      toast.error('Please select an organization or person')
      return
    }

    const orgRoles = state.selectedOrganization?.roles || null
    const personRoles =
      orgRoles?.length > 0 && Array.isArray(orgRoles)
        ? orgRoles
            .filter((personRole) => !personRole.role?.includes('OWNER_EMAIL'))
            .map((personRole) => ({
              personEmail: personRole.email,
              roles: Array.isArray(personRole.role)
                ? personRole.role
                : [personRole.role].filter(Boolean),
              reasoning: personRole.reasoningForRole,
            }))
        : state.selectedPerson
          ? [
              {
                personEmail: state.selectedPerson.objectId,
                roles: ['PRIMARY_CONTACT'],
                reasoning:
                  'Identified as primary contact in Opportunity creation process.',
              },
            ]
          : null

    const input: OpportunityCreateInput = {
      title: state.title,
      type: state.oppType,
      ownerEmail: state.selectedOwner.email,
      expectedCloseDate: state.expectedCloseDate.toISOString(),
      expectedRevenue: state.expectedRevenue,
      hasRevenue: !!state.expectedRevenue,
      domain:
        state.domain ||
        state.selectedOrganization?.domain ||
        extractEmailDomain(state.selectedPerson?.objectId),
      position: 0,
      stageId: state.selectedStage.id,
      pipelineId: state.selectedPipeline.id,
      workspaceId: selectedWorkspace || workspaceId,
      primaryPerson: state.selectedPerson?.objectId || null,
      roles: personRoles,
      currentStatus: '',
    }

    logger.dev('Creating opportunity with input:', input)

    await toast.promise(
      createOpportunityForDialog({
        variables: { input },
      }),
      {
        loading: 'Creating opportunity...',
        success: (result) => {
          dispatch({ type: 'RESET' })
          if (onComplete) onComplete()

          // Open the newly created opportunity in the sidebar
          setSidebarObject({
            objectType: NativeObjectTypes.Opportunity,
            objectId: result.data.createOpportunity.id,
            properties: result.data.createOpportunity,
            workspaceId: result.data.createOpportunity.workspaceId,
          })

          return 'Opportunity created!'
        },
        error: (error) => {
          logger.error('Failed to create opportunity:', error)
          return 'Error creating opportunity'
        },
      }
    )
  }, [
    state,
    selectedWorkspace,
    workspaceId,
    createOpportunityForDialog,
    onComplete,
    setSidebarObject,
  ])

  const handleClearSelection = async (type: 'organization' | 'person') => {
    try {
      await confirm({
        title: `Clear ${type === 'organization' ? 'Organization' : 'Person'}?`,
        description: `Are you sure you want to clear the selected ${type}? You'll need to select a new one.`,
        confirmationText: 'Clear',
        cancellationText: 'Keep',
      })

      if (type === 'organization') {
        dispatch({ type: 'SET_ORGANIZATION', payload: null })
        dispatch({ type: 'SET_DOMAIN', payload: null })
      } else {
        dispatch({ type: 'SET_PERSON', payload: null })
      }
      setSearchOpen(true)
    } catch {
      // User cancelled
    }
  }

  const handleDismissOwner = async () => {
    try {
      await confirm({
        title: 'Remove Owner?',
        description:
          'Are you sure you want to remove the owner? You can select a new one.',
        confirmationText: 'Remove',
        cancellationText: 'Keep',
      })

      dispatch({ type: 'DISMISS_OWNER' })
    } catch {
      // User cancelled
    }
  }

  return (
    <Box sx={STYLES.container}>
      <Box sx={STYLES.header}>
        <Row sx={STYLES.headerRow}>
          <Typography variant="h3">
            {mode === 'create' ? 'Create' : 'Edit'} Opportunity
          </Typography>
        </Row>
      </Box>

      <Box sx={STYLES.content}>
        {/* Only show search button if no object selected */}
        {!state.selectedOrganization && !state.selectedPerson && (
          <Box sx={STYLES.finderBox}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setSearchOpen(true)}
              startIcon={<Search />}
            >
              Search for Organization or Person
            </Button>
          </Box>
        )}

        <SearchModal
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={handleSearchSelect}
          forcedObjectTypes={[
            NativeObjectTypes.Organization,
            NativeObjectTypes.Contact,
          ]}
          allowCreation={true}
          workspaceId={selectedWorkspace || workspaceId}
        />

        {state.selectedOrganization && (
          <Box sx={STYLES.selectedObject}>
            <Row sx={STYLES.objectTileContainer}>
              <Box sx={STYLES.objectTileBox}>
                <ObjectTile
                  objectId={
                    state.selectedOrganization.domain ||
                    state.selectedOrganization.objectId
                  }
                  objectType={NativeObjectTypes.Organization}
                  workspaceId={selectedWorkspace || workspaceId}
                  properties={state.selectedOrganization}
                  size={48}
                />
              </Box>
              <IconButton
                onClick={() => handleClearSelection('organization')}
                size="small"
              >
                <Close />
              </IconButton>
            </Row>
          </Box>
        )}

        {state.selectedPerson && (
          <Box sx={STYLES.selectedObject}>
            <Row sx={STYLES.objectTileContainer}>
              <Box sx={STYLES.objectTileBox}>
                <ObjectTile
                  objectId={state.selectedPerson.objectId}
                  objectType={state.selectedPerson.objectType}
                  workspaceId={selectedWorkspace || workspaceId}
                  properties={state.selectedPerson}
                  size={48}
                />
              </Box>
              <IconButton
                onClick={() => handleClearSelection('person')}
                size="small"
              >
                <Close />
              </IconButton>
            </Row>
          </Box>
        )}

        <OpportunityCreateForm
          state={state}
          dispatch={dispatch}
          pipelines={contextPipelines || []}
          loading={false}
          onSubmit={handleOpportunityCreate}
          onDismissOwner={handleDismissOwner}
        />
      </Box>
    </Box>
  )
}

export default OpportunityEditor
